import { ArrowRightOutlined, FileAddOutlined } from '@ant-design/icons'
import { Button, Form, Input } from 'antd'
import { t } from 'i18next'
import { memo, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { ModalBase, SelectWorkspaceFile } from '../../../../../components'
import { workspaceDocumentSetStatuses } from '../../../../../constants/constants'
import { useWorkspaceDetails } from '../../../../../hooks'
import useWorkspaceFolders from '../../../../../hooks/useWorkspaceFolders'
import { notificationService } from '../../../../../services'
import { RootState } from '../../../../../store'
import { ConformityChecksConfigDocumentSet } from '../../../../../store/reducers/workspaceReducer'

const StyledForm = styled(Form)`
  margin-top: 8px;

  .ant-form-item-label {
    font-weight: 500;
  }
`
const StyledButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

interface FormValues {
  title: string
  documentId: string
  owner: string
}

interface ExtendedConformityChecksConfigDocumentSet
  extends ConformityChecksConfigDocumentSet {}

interface WorkspaceAddDocumentModalProps {
  disabled?: boolean
}

const WorkspaceAddDocumentModal = ({
  disabled,
}: WorkspaceAddDocumentModalProps) => {
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false)
  const [form] = Form.useForm()
  const { workspaceId } = useParams()

  const currentUser = useSelector((state: RootState) => state.user.currentUser)

  const navigate = useNavigate()

  const { updateWorkspaceDetails, selectedWorkspace, refreshWorkspaceDetails } =
    useWorkspaceDetails({
      workspaceId: workspaceId,
      preventFetch: true,
    })

  const { workspaceFiles } = useWorkspaceFolders({
    workspaceId: workspaceId,
    preventFetch: true,
  })

  const defaultValues = {
    title: '',
    documentId: '',
    owner: currentUser?.id || '',
  }

  const existingDocuments = useMemo(() => {
    if (!selectedWorkspace?.config?.conformityChecksConfig?.documents) {
      return []
    }
    return selectedWorkspace.config.conformityChecksConfig
      .documents as ExtendedConformityChecksConfigDocumentSet[]
  }, [selectedWorkspace])

  const disabledDocumentIds = useMemo(() => {
    return existingDocuments.map((doc) => doc.id)
  }, [existingDocuments])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      await handleAddDocument(values)
      form.resetFields()
    } catch (err) {
      notificationService.notificationError({
        messageToDisplay: t(
          'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.validationError'
        ),
      })
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setIsAddDocumentModalOpen(false)
  }

  const handleAddDocument = async (values: FormValues) => {
    if (!selectedWorkspace) {
      console.error('No selected workspace')
      return
    }
    if (!selectedWorkspace.config) {
      console.error('No config for selected workspace')
      return
    }

    const documents =
      selectedWorkspace.config.conformityChecksConfig?.documents || []

    const workspaceFile = workspaceFiles?.find(
      (file) => file.id === values.documentId
    )

    const document = {
      id: values.documentId,
      path: workspaceFile?.path || '',
      webUrl: workspaceFile?.webUrl || '',
      creationDate: new Date().toISOString(),
      title: values.title,
      documentSetStatus: workspaceDocumentSetStatuses.OPENED,
      ownerId: currentUser?.id || '',
    } as ExtendedConformityChecksConfigDocumentSet

    const updatedConfig = {
      ...selectedWorkspace.config.conformityChecksConfig,
      documents: [...documents, document],
    }

    try {
      await updateWorkspaceDetails({
        ...selectedWorkspace.config,
        conformityChecksConfig: updatedConfig,
      })

      notificationService.notificationSuccess({
        messageToDisplay: t(
          'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.successCreation'
        ),
      })
      refreshWorkspaceDetails()
      setIsAddDocumentModalOpen(false)
    } catch (error) {
      notificationService.notificationError({
        messageToDisplay: t(
          'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.errorCreation'
        ),
      })
      console.error('Error updating workspace details', error)
    }
  }

  const handleOpenModal = useCallback(() => {
    if (!selectedWorkspace?.config?.ofrDocumentTemplates) {
      notificationService.notificationError({
        messageToDisplay: (
          <StyledButtonContainer>
            <span>
              {t(
                'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.missingTemplates'
              )}
            </span>
            <Button
              icon={<ArrowRightOutlined />}
              ghost
              danger
              size="small"
              onClick={() =>
                navigate(`/workspaceDetails/${workspaceId}/settings/info`)
              }
            >
              {t(
                'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.goToSettings'
              )}
            </Button>
          </StyledButtonContainer>
        ),
      })
      return
    }
    setIsAddDocumentModalOpen(true)
    form.resetFields()
  }, [selectedWorkspace, form, navigate, workspaceId])

  return (
    <>
      <Button
        type="primary"
        icon={<FileAddOutlined />}
        onClick={handleOpenModal}
        disabled={disabled}
      >
        {t('workspaceDetailsPage.workspaceDocumentsPanel.createDocument')}
      </Button>
      <ModalBase
        title={t(
          'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.title'
        )}
        open={isAddDocumentModalOpen}
        onCancel={handleCancel}
        okText={t(
          'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.createButton'
        )}
        cancelText={t(
          'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.cancelButton'
        )}
        onOk={handleSubmit}
      >
        <StyledForm
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={defaultValues}
        >
          <Form.Item
            name="title"
            label={t(
              'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.documentTitle'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.documentTitleRequired'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.documentTitlePlaceholder'
              )}
            />
          </Form.Item>

          <Form.Item
            name="documentId"
            label={t(
              'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.folderLabel'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.folderRequired'
                ),
              },
            ]}
            extra={t(
              'workspaceDetailsPage.workspaceDocumentsPanel.addDocumentModal.extraDocumentField'
            )}
          >
            <SelectWorkspaceFile
              workspaceId={workspaceId}
              preventFetch={!isAddDocumentModalOpen}
              disabledIds={disabledDocumentIds}
            />
          </Form.Item>
        </StyledForm>
      </ModalBase>
    </>
  )
}

export default memo(WorkspaceAddDocumentModal)
